import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '@blimo/shared';
import { Form, FormControl, FormField, FormItem, FormLabel, FormRootError, InputField, applyProblemToForm } from '../components/form.jsx';
import { Checkbox } from '../components/input.jsx';
import { Button } from '../components/button.jsx';

/**
 * @param {{
 *   onSubmit: (values: { email: string, password: string, remember: boolean }) => Promise<unknown>,
 *   forgotPasswordLink?: import('react').ReactNode,
 *   defaultEmail?: string,
 * }} props
 */
export function LoginForm({ onSubmit, forgotPasswordLink, defaultEmail = '' }) {
  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: defaultEmail, password: '', remember: false },
  });

  async function submit(values) {
    try {
      await onSubmit(values);
    } catch (error) {
      // Generic message on failed login (no account enumeration) – but keep throttling hints.
      if (error?.status === 422 && error?.errors?.email) {
        form.setError('root.server', { type: 'server', message: error.errors.email[0] });
      } else {
        applyProblemToForm(error, form.setError, { fields: [] });
      }
      form.resetField('password');
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
        <FormRootError />
        <InputField name="email" label="E-Mail-Adresse" type="email" autoComplete="username" inputMode="email" autoFocus />
        <InputField name="password" label="Passwort" type="password" autoComplete="current-password" />
        <div className="flex items-center justify-between gap-2">
          <FormField
            name="remember"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex-row items-center gap-2">
                <FormControl>
                  <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                </FormControl>
                <FormLabel className="font-normal">Angemeldet bleiben</FormLabel>
              </FormItem>
            )}
          />
          {forgotPasswordLink}
        </div>
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Anmelden
        </Button>
      </form>
    </Form>
  );
}
